import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Edit2, Trash2, Plus, X, Save, DoorOpen, Loader, Home, Layers, Users, type LucideIcon } from 'lucide-react';
import {
  UnitFormFields,
  UnitFormState,
  buildUnitName,
  useUnitSectionRequired,
} from '../molecules/UnitFormFields';
import { useI18n } from '@/shared/i18n';
import { useServices, useDeleteConfirmation, useSection, useToast } from '@/app/providers';
import { AccessDevice, SectionStatus, Unit, UnitType } from '@/shared/types';
import { useAbility } from '@/modules/identity/application/ability';
import { Button } from '../atoms/Button';
import { usePaginatedList, useCrudNotifications } from '@/shared/hooks';
import { DataTable, SearchableSelect, type ColumnDef } from '@/core/ui/components';
import { SectionSelector } from '@/modules/sections/ui/components/SectionSelector';
import { UnitAllowedDevicesModal } from './UnitAllowedDevicesModal';

const UNIT_TYPE_META: Record<UnitType, { icon: LucideIcon; className: string }> = {
  [UnitType.APARTMENT]: {
    icon: Building2,
    className: 'bg-primary-600/20 text-primary-800 dark:bg-primary-500/30 dark:text-primary-100',
  },
  [UnitType.HOUSE]: {
    icon: Home,
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/30 dark:text-amber-100',
  },
};

/**
 * Centinela de "sin sección" para el filtro (`GET /units?sectionId=none`):
 * en el select hace falta un valor real porque `null` ya significa "nada
 * elegido". Solo filtra; las unidades ya no se pueden desasignar (SECT_1012).
 */
const UNASSIGNED = 'none';

export const AdminUnitList = () => {
  const { t } = useI18n();
  const { confirmDelete } = useDeleteConfirmation();
  const toast = useToast();
  const { unitRepo, deviceRepo } = useServices();
  const { sections, selectedSectionId, hasSections, isSectionAdmin } = useSection();
  const sectionRequired = useUnitSectionRequired();
  // ?search= llega desde la búsqueda global del encabezado.
  const urlSearch = useSearchParams()[0].get('search');
  const ability = useAbility();
  const canUseUnitAction = (action: 'create' | 'update' | 'delete') =>
    ability.can('manage', 'all') || ability.can(action, 'Unit');
  const canCreateUnit = canUseUnitAction('create');
  const canUpdateUnit = canUseUnitAction('update');
  const canDeleteUnit = canUseUnitAction('delete');
  // Permiso separado de "Puntos de acceso" (AccessDevice): permite ver/asignar/quitar
  // puertas de una unidad sin dar acceso al módulo de gestión de dispositivos.
  const canManageAllUnitDevices = ability.can('manage', 'all');
  const canViewUnitDevices = canManageAllUnitDevices || ability.can('read', 'UnitAllowedDevice');
  const canAddUnitDevices = canManageAllUnitDevices || ability.can('update', 'UnitAllowedDevice');
  const canRemoveUnitDevices = canManageAllUnitDevices || ability.can('delete', 'UnitAllowedDevice');
  const { withCreateNotification, withUpdateNotification, withDeleteNotification } = useCrudNotifications({
    entityName: 'Unidad',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Partial<Unit>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [allowedDevicesUnit, setAllowedDevicesUnit] = useState<Unit | null>(null);
  const [allDevices, setAllDevices] = useState<AccessDevice[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [originalDeviceIds, setOriginalDeviceIds] = useState<string[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [loadingUnitExtras, setLoadingUnitExtras] = useState(false);
  const [allowedDeviceCounts, setAllowedDeviceCounts] = useState<Record<string, number>>({});
  const [residentNames, setResidentNames] = useState<Record<string, string[]>>({});

  // Pagination hook with debounced search
  const {
    data: units,
    meta,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = usePaginatedList({
    fetchFn: (params) =>
      unitRepo.getUnits({
        ...params,
        sectionId:
          typeof params.sectionId === 'string' && params.sectionId ? params.sectionId : undefined,
      }),
    initialFilters: { sectionId: selectedSectionId ?? '' },
    initialSearch: urlSearch ?? '',
    errorTitle: 'Error al cargar unidades',
  });

  // La lista sigue al selector de sección del contexto, también cuando este se
  // resuelve después del montaje (sección guardada al recargar). El `fetchFn`
  // no cae al contexto: el filtro explícito ('' = todas) siempre gana.
  useEffect(() => {
    const sectionId = selectedSectionId ?? '';
    if (filters.sectionId !== sectionId) setFilters({ sectionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectionId]);

  // Elegir otro resultado de la búsqueda global estando ya en esta página.
  useEffect(() => {
    if (urlSearch !== null) setSearch(urlSearch);
  }, [urlSearch, setSearch]);

  // Selección para la reasignación masiva. Es el camino de migración de un
  // residencial que ya tenía unidades: el backfill por `tower` no cubre casas.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSectionId, setBulkSectionId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // La selección se descarta al cambiar de página, filtro o búsqueda: las
  // filas ya no son las mismas y mantenerla asignaría lo que no se ve.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, search, filters.sectionId, pageSize]);

  const toggleSelected = (unitId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const allOnPageSelected = units.length > 0 && units.every((u) => selectedIds.has(u.id));

  const handleBulkAssign = async () => {
    if (!canUpdateUnit || selectedIds.size === 0 || !bulkSectionId) return;
    setIsAssigning(true);
    try {
      const { updated } = await unitRepo.assignUnitsToSection([...selectedIds], bulkSectionId);
      toast.success(
        updated === 0
          ? 'Las unidades seleccionadas ya estaban en esa sección'
          : `${updated} unidad(es) reasignadas. Sus residentes pasan a la sección automáticamente.`,
      );
      setSelectedIds(new Set());
      setBulkSectionId(null);
      refresh();
    } catch (error) {
      // El mensaje del servidor distingue las causas que importan: 422 sección
      // inactiva, 403 fuera de perímetro, 404 unidad inexistente. Tragárselo
      // deja al admin sin saber qué corregir.
      const detail = error instanceof Error ? error.message : '';
      toast.error(detail ? `No se pudo reasignar la sección: ${detail}` : 'No se pudo reasignar la sección');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleOpenCreate = () => {
    if (!canCreateUnit) return;

    const section = sections.find((s) => s.id === selectedSectionId);

    setEditingUnit({
      type: UnitType.APARTMENT,
      tower: section?.name ?? '',
      sectionId: selectedSectionId ?? undefined,
      floor: '',
      number: '',
      street: '',
      block: '',
      exteriorNumber: ''
    });
    setSelectedDeviceIds([]);
    setOriginalDeviceIds([]);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (unit: Unit) => {
    if (!canUpdateUnit) return;

    setEditingUnit({ ...unit });
    setSelectedDeviceIds([]);
    setOriginalDeviceIds([]);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!isModalOpen) return;

    if (!canViewUnitDevices) return;

    const loadExtras = async () => {
      setLoadingUnitExtras(true);
      try {
        const devices = await deviceRepo.getAllDevices();
        setAllDevices(devices);

        if (isEditMode && editingUnit.id) {
          const allowed = await unitRepo.getAllowedDevices(editingUnit.id);
          const ids = allowed.map((device) => device.id);
          setSelectedDeviceIds(ids);
          setOriginalDeviceIds(ids);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'No se pudieron cargar datos de la unidad';
        toast.error(message);
      } finally {
        setLoadingUnitExtras(false);
      }
    };

    loadExtras();
  }, [canViewUnitDevices, deviceRepo, editingUnit.id, isEditMode, isModalOpen, toast, unitRepo]);

  useEffect(() => {
    const unitIds = units.map((unit) => unit.id).filter(Boolean);
    if (unitIds.length === 0) {
      setAllowedDeviceCounts({});
      return;
    }

    let cancelled = false;

    const loadAllowedDeviceCounts = async () => {
      const results = await Promise.all(
        unitIds.map(async (unitId) => {
          try {
            const devices = await unitRepo.getAllowedDevices(unitId);
            return [unitId, devices.length] as const;
          } catch {
            return [unitId, 0] as const;
          }
        }),
      );

      if (!cancelled) {
        setAllowedDeviceCounts(Object.fromEntries(results));
      }
    };

    loadAllowedDeviceCounts();

    return () => {
      cancelled = true;
    };
  }, [unitRepo, units]);

  // ponytail: una petición por unidad de la página (como las puertas); un campo
  // agregado en el listado si la página crece más allá de 100.
  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      units.map(async (unit) => {
        try {
          const members = await unitRepo.getUnitUsers(unit.id);
          return [unit.id, members.map((m) => m.memberName || 'Sin nombre')] as const;
        } catch {
          return [unit.id, []] as const;
        }
      }),
    ).then((results) => {
      if (!cancelled) setResidentNames(Object.fromEntries(results));
    });
    return () => {
      cancelled = true;
    };
  }, [unitRepo, units]);

  const refreshAllowedDeviceCount = async (unitId: string) => {
    try {
      const devices = await unitRepo.getAllowedDevices(unitId);
      setAllowedDeviceCounts((current) => ({ ...current, [unitId]: devices.length }));
    } catch {
      setAllowedDeviceCounts((current) => ({ ...current, [unitId]: 0 }));
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!canDeleteUnit) return;

    await confirmDelete({
      itemName: name,
      itemType: 'Unidad',
      onConfirm: async () => {
        await withDeleteNotification(() => unitRepo.deleteUnit(id));
        refresh();
      }
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode ? !canUpdateUnit : !canCreateUnit) return;
    if (sectionRequired && !editingUnit.sectionId) {
      toast.error('Selecciona la sección de la unidad');
      return;
    }

    try {
      const payload: any = { ...editingUnit, name: buildUnitName(editingUnit as UnitFormState) };

      let savedUnit: Unit;

      if (isEditMode && editingUnit.id) {
        savedUnit = await withUpdateNotification(() => unitRepo.updateUnit(payload));
      } else {
        savedUnit = await withCreateNotification(() => unitRepo.createUnit(payload));
      }

      const toAdd = canAddUnitDevices
        ? selectedDeviceIds.filter((id) => !originalDeviceIds.includes(id))
        : [];
      const toRemove = isEditMode && canRemoveUnitDevices
        ? originalDeviceIds.filter((id) => !selectedDeviceIds.includes(id))
        : [];

      await Promise.all([
        ...toAdd.map((deviceId) => unitRepo.addAllowedDevice(savedUnit.id, deviceId)),
        ...toRemove.map((deviceId) => unitRepo.removeAllowedDevice(savedUnit.id, deviceId)),
      ]);

      if (toAdd.length > 0 || toRemove.length > 0) {
        toast.success('Puertas de acceso actualizadas');
      }
      setAllowedDeviceCounts((current) => ({ ...current, [savedUnit.id]: selectedDeviceIds.length }));
      setIsModalOpen(false);
      refresh();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const selectedDevices = useMemo(
    () => allDevices.filter((device) => selectedDeviceIds.includes(device.id)),
    [allDevices, selectedDeviceIds],
  );

  const availableDeviceOptions = useMemo(
    () =>
      allDevices
        .filter((device) => !selectedDeviceIds.includes(device.id))
        .map((device) => ({
          value: device.id,
          label: `${device.name}${device.location ? ` - ${device.location}` : ''}`,
        })),
    [allDevices, selectedDeviceIds],
  );

  const handleAddSelectedDevice = () => {
    if (!selectedDeviceId) return;
    setSelectedDeviceIds((current) => [...current, selectedDeviceId]);
    setSelectedDeviceId('');
  };

  const sectionNameById = useMemo(() => new Map(sections.map((s) => [s.id, s.name])), [sections]);

  // Column definitions for DataTable
  const columns: ColumnDef<Unit>[] = useMemo(
    () => [
      ...(hasSections && canUpdateUnit
        ? [
            {
              id: 'select',
              header: (
                <input
                  type="checkbox"
                  aria-label="Seleccionar todas las unidades de esta página"
                  checked={allOnPageSelected}
                  onChange={() =>
                    setSelectedIds(allOnPageSelected ? new Set() : new Set(units.map((u) => u.id)))
                  }
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
              ),
              cell: (unit: Unit) => (
                <input
                  type="checkbox"
                  aria-label={`Seleccionar ${unit.name}`}
                  checked={selectedIds.has(unit.id)}
                  onChange={() => toggleSelected(unit.id)}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
              ),
            } satisfies ColumnDef<Unit>,
          ]
        : []),
      {
        id: 'type',
        header: 'Tipo',
        cell: (unit) => {
          const meta = UNIT_TYPE_META[unit.type];
          const Icon = meta.icon;
          return (
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${meta.className}`}>
                <Icon size={16} />
              </div>
              <span className="font-medium">
                {unit.type === UnitType.APARTMENT ? t('admin.units.apt') : t('admin.units.house')}
              </span>
            </div>
          );
        },
      },
      {
        id: 'location',
        header: 'Unidad / Ubicaci\u00f3n',
        cell: (unit) => {
          const primaryParts: string[] = [];
          if (unit.type === UnitType.APARTMENT) {
            if (unit.tower) primaryParts.push(unit.tower);
            primaryParts.push(`Depto ${unit.number ?? ''}`.trim());
          } else {
            if (unit.street) primaryParts.push(unit.street);
            primaryParts.push(`#${unit.exteriorNumber ?? ''}`);
          }
          if (unit.interiorNumber) primaryParts.push(`Int. ${unit.interiorNumber}`);

          const secondaryParts: string[] = [];
          if (unit.type === UnitType.APARTMENT && unit.floor) {
            secondaryParts.push(`Piso ${unit.floor}`);
          }
          if (unit.block) secondaryParts.push(`Manzana ${unit.block}`);
          const sectionName = unit.sectionId ? sectionNameById.get(unit.sectionId) : null;
          if (sectionName) secondaryParts.push(sectionName);

          return (
            <div className="space-y-0.5">
              <div className="text-sm font-semibold text-slate-800 tabular-nums">
                {primaryParts.join(' • ')}
              </div>
              {secondaryParts.length > 0 && (
                <div className="text-xs text-slate-500">{secondaryParts.join(' • ')}</div>
              )}
              {hasSections && !unit.sectionId && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  Sin sección
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'residents',
        header: 'Residentes',
        cell: (unit) => {
          const names = residentNames[unit.id];
          if (names === undefined) return <span className="text-xs text-slate-400">...</span>;
          if (names.length === 0) return <span className="text-xs text-slate-400">Sin residentes</span>;
          return (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                <Users size={13} />
                {names.length}
              </span>
              {canUpdateUnit && (
                <button
                  type="button"
                  onClick={() => handleOpenEdit(unit)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  Ver
                </button>
              )}
            </div>
          );
        },
      },
      {
        id: 'allowedDevices',
        header: 'Puertas',
        cell: (unit) => {
          const count = allowedDeviceCounts[unit.id];
          const hasAccess = typeof count === 'number' && count > 0;
          return (
            <span
              aria-label={`${count ?? 0} puertas asignadas`}
              className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold min-w-[2rem] ${
                hasAccess
                  ? 'border-indigo-100 bg-indigo-50 text-indigo-700'
                  : 'border-slate-200 bg-slate-50 text-slate-400'
              }`}
            >
              <DoorOpen size={13} />
              {count === undefined ? '...' : count}
            </span>
          );
        },
      },
    ],
    [allowedDeviceCounts, residentNames, sectionNameById, t, hasSections, canUpdateUnit, selectedIds, allOnPageSelected, units]
  );

  return (
    <>
      {selectedIds.size > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="text-sm font-semibold text-primary-800">
            {selectedIds.size} unidad(es) seleccionadas
          </span>
          <div className="flex flex-1 items-center gap-2">
            <SearchableSelect
              options={sections
                .filter((section) => section.status === SectionStatus.ACTIVE)
                .map((section) => ({ value: section.id, label: section.name }))}
              value={bulkSectionId}
              onChange={setBulkSectionId}
              placeholder="Asignar a sección..."
              isSearchable={false}
              className="w-56"
            />
            <Button
              onClick={handleBulkAssign}
              disabled={!bulkSectionId || isAssigning}
              icon={<Layers size={16} />}
            >
              {isAssigning ? 'Aplicando…' : 'Asignar'}
            </Button>
            <Button variant="secondary" onClick={() => setSelectedIds(new Set())}>
              Cancelar
            </Button>
          </div>
          <p className="text-xs text-primary-700 sm:ml-auto">
            Los residentes de cada unidad pasan a la sección automáticamente.
          </p>
        </div>
      )}

      <DataTable
        data={units}
        columns={columns}
        keyExtractor={(unit) => unit.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title={t('admin.units.title')}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar unidad..."
        loading={loading}
        filters={
          hasSections && (
            <div className="flex items-center gap-2">
              <SectionSelector
                variant="slate"
                className="w-48"
                onSelectionChange={(id) => setFilters({ sectionId: id ?? '' })}
              />
              {/*
                `none` lo resuelve el backend como "sin asignar" y solo está
                disponible para el admin global, así que no se ofrece al
                SECTION_ADMIN, que nunca puede ver una unidad sin sección.
              */}
              {!isSectionAdmin && (
                <button
                  type="button"
                  onClick={() =>
                    setFilters({
                      sectionId: filters.sectionId === UNASSIGNED ? '' : UNASSIGNED,
                    })
                  }
                  aria-pressed={filters.sectionId === UNASSIGNED}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                    filters.sectionId === UNASSIGNED
                      ? 'border-amber-400 bg-amber-50 text-amber-700'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  Sin sección
                </button>
              )}
            </div>
          )
        }
        emptyMessage="No hay unidades registradas"
        emptyIcon={(
          <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
            <Building2 className="w-8 h-8 text-primary-400" />
          </div>
        )}
        actions={canCreateUnit ? (
          <Button onClick={handleOpenCreate} icon={<Plus size={16} />}>
            {t('admin.units.create')}
          </Button>
        ) : undefined}
        rowActions={(unit) => (
          <>
            {canViewUnitDevices && (
              <button
                onClick={() => setAllowedDevicesUnit(unit)}
                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                title={t('admin.units.allowed_devices.button')}
                aria-label={t('admin.units.allowed_devices.button')}
              >
                <DoorOpen size={16} />
              </button>
            )}
            {canUpdateUnit && (
              <button
                onClick={() => handleOpenEdit(unit)}
                className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
              >
                <Edit2 size={16} />
              </button>
            )}
            {canDeleteUnit && (
              <button
                onClick={() => handleDelete(unit.id, unit.name)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          </>
        )}
      />

      {allowedDevicesUnit && (
        <UnitAllowedDevicesModal
          unit={allowedDevicesUnit}
          onClose={() => {
            const unitId = allowedDevicesUnit.id;
            setAllowedDevicesUnit(null);
            refreshAllowedDeviceCount(unitId);
          }}
        />
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{isEditMode ? t('common.edit') : t('admin.units.create')}</h3>
              <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-slate-400"/></button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-5 overflow-y-auto">
              <UnitFormFields
                value={editingUnit as UnitFormState}
                onChange={(next) => setEditingUnit({ ...editingUnit, ...next })}
              />

              {isEditMode && editingUnit.id && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <h4 className="font-bold text-slate-800 flex items-center gap-2 mb-3">
                    <Users size={16} /> Residentes asignados
                  </h4>
                  {(residentNames[editingUnit.id] ?? []).length === 0 ? (
                    <p className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-3 text-center">
                      Esta unidad no tiene residentes asignados.
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {residentNames[editingUnit.id].map((name, i) => (
                        <li key={`${name}-${i}`} className="text-sm text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2">
                          {name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {canViewUnitDevices && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h4 className="font-bold text-slate-800 flex items-center gap-2">
                        <DoorOpen size={16} /> Puertas de acceso remoto
                      </h4>
                      <p className="text-xs text-slate-500">Selecciona las puertas que podrá abrir esta unidad.</p>
                    </div>
                    {loadingUnitExtras && <Loader size={16} className="text-slate-400 animate-spin" />}
                  </div>

                  {canAddUnitDevices && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SearchableSelect
                          value={selectedDeviceId || null}
                          onChange={value => setSelectedDeviceId(value ?? '')}
                          placeholder={availableDeviceOptions.length === 0 ? 'No hay puertas disponibles' : 'Seleccionar puerta...'}
                          options={availableDeviceOptions}
                          isDisabled={loadingUnitExtras || availableDeviceOptions.length === 0}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        icon={<Plus size={16} />}
                        onClick={handleAddSelectedDevice}
                        disabled={!selectedDeviceId}
                      >
                        Agregar
                      </Button>
                    </div>
                  )}

                  <div className="mt-3 space-y-2">
                    {selectedDevices.length === 0 ? (
                      <p className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-3 text-center">
                        Sin puertas asignadas.
                      </p>
                    ) : (
                      selectedDevices.map((device) => (
                        <div key={device.id} className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                          <div className="min-w-0">
                            <p className="font-medium text-sm text-slate-800 truncate">{device.name}</p>
                            <p className="text-xs text-slate-500 truncate">{device.location || device.externalDeviceId}</p>
                          </div>
                          {canRemoveUnitDevices && (
                            <button
                              type="button"
                              onClick={() => setSelectedDeviceIds((current) => current.filter((id) => id !== device.id))}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.close')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1" icon={<Save size={16}/>}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
